import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Autocomplete,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Route, RouteOptionType, TripBasicData } from "../types";
import routeApi from "../../../masterData/route/apiService";
import { useToast } from "../../../../components/Toast";
import {
  commonButtonProps,
  commonDateFieldProps,
  commonTextFieldProps,
} from "../../../../utils/styles";
import { getValidToken } from "../../../../utils/authUtils";
import axios from "axios";
import { Loading } from "../../../../components/Loading";

const validationSchema = Yup.object().shape({
  journeyStartDate: Yup.string().required("Start date is required"),
  journeyReturnDate: Yup.string().when("tripType", {
    is: "Round",
    then: (schema) =>
      schema
        .required("Return date is required")
        .test(
          "is-after-start-date",
          "Return date must be after start date",
          function (value) {
            const { journeyStartDate } = this.parent;
            if (!value || !journeyStartDate) return true; // skip if not both filled
            return new Date(value) > new Date(journeyStartDate);
          }
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  vehicleOdometerStart: Yup.number()
    .typeError("Must be a number")
    .optional()
    .nullable(),
  vehicleOdometerEnd: Yup.number()
    .typeError("Must be a number")
    .optional()
    .test(
      "is-greater-than-start",
      "End KMs must be greater than Start KMs",
      function (end) {
        const { vehicleOdometerStart: start } = this.parent;

        // Don't validate if either is undefined or null
        if (start == null || end == null) return true;

        return end > start;
      }
    )
    .nullable(),
  remark: Yup.string().optional().nullable(),
});

const handleSave = async (
  values: TripBasicData,
  showToast: any,
  setIsSubmitting: any,
  onClose: any,
  fetchTripData?: () => void
) => {
  try {
    const token = await getValidToken();
    const url = `${process.env.REACT_APP_API_HOST}trips/${values.id}`;
    const { changeLogs, ...rest } = values;
    const res = await axios.put(url, rest, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (res?.data?.isSuccess) {
      showToast("Trip basic data saved successfully", "success");
      fetchTripData?.();
    } else {
      throw res?.data?.message;
    }
  } catch (error) {
    console.error("Error saving trip basic data:", error);
    showToast("Failed to save trip basic data", "error");
  } finally {
    setIsSubmitting(false);
    onClose();
  }
};

const BasicDetailsEditModal = ({
  tripBasicData,
  open = true,
  onClose,
  fetchTripData,
}: {
  tripBasicData: TripBasicData;
  open?: boolean;
  onClose?: () => void;
  fetchTripData?: () => void;
}) => {
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (isSubmitting) {
    return <Loading message="Saving trip basic data..." fullScreen={true} />;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem" }}>Route Details</DialogTitle>
      <Formik
        initialValues={tripBasicData}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleSave(values, showToast, setIsSubmitting, onClose, fetchTripData);
        }}
      >
        {({ values, errors, touched, handleChange, setFieldValue }) => (
          <Form>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Return Date"
                    name="journeyReturnDate"
                    type="date"
                    value={
                      values.journeyReturnDate
                        ? new Date(values.journeyReturnDate)
                            .toISOString()
                            .split("T")[0]
                        : ""
                    }
                    onChange={handleChange}
                    error={
                      touched.journeyReturnDate &&
                      Boolean(errors.journeyReturnDate)
                    }
                    helperText={
                      touched.journeyReturnDate && errors.journeyReturnDate
                    }
                    fullWidth
                    {...commonDateFieldProps}
                    disabled={values.tripType !== "Round"}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Start KMs Reading"
                    name="vehicleOdometerStart"
                    value={values.vehicleOdometerStart}
                    onChange={handleChange}
                    error={
                      touched.vehicleOdometerStart &&
                      Boolean(errors.vehicleOdometerStart)
                    }
                    helperText={
                      touched.vehicleOdometerStart &&
                      errors.vehicleOdometerStart
                    }
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <span style={{ fontSize: "0.75rem" }}>KMs</span>
                      ),
                    }}
                    type="number"
                    {...commonTextFieldProps}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="End KMs Reading"
                    name="vehicleOdometerEnd"
                    value={values.vehicleOdometerEnd}
                    onChange={handleChange}
                    error={
                      touched.vehicleOdometerEnd &&
                      Boolean(errors.vehicleOdometerEnd)
                    }
                    helperText={
                      touched.vehicleOdometerEnd && errors.vehicleOdometerEnd
                    }
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <span style={{ fontSize: "0.75rem" }}>KMs</span>
                      ),
                    }}
                    type="number"
                    {...commonTextFieldProps}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="Remarks (Optional)"
                    name="remark"
                    value={values.remark}
                    onChange={handleChange}
                    fullWidth
                    {...commonTextFieldProps}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                size="small"
                sx={{ height: 20, fontSize: "0.75rem" }}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                sx={{ height: 20, fontSize: "0.75rem" }}
                type="submit"
                color="primary"
              >
                Save
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default BasicDetailsEditModal;
